/**
 * BrandingContext provides organization-specific branding (colors, logo)
 * based on the authenticated user's Auth0 organization.
 *
 * It reads orgName from Redux state, looks up the branding config,
 * applies CSS custom properties to :root, and exposes branding via context.
 */
import {
  type ComponentType,
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useAppSelector } from "@financial-advisory/data-access/financial-advisory-api-service";
import {
  type BrandingColors,
  type LogoProps,
  brandingByTenantId,
  defaultBranding,
} from "@targeted-support/config";
import { UnlikelyAILogo } from "@targeted-support/ui/components/icons";

export interface BrandingContextValue {
  tenantId: string | undefined;
  /**
   * What to call the customer on screen, e.g. "NatWest".
   */
  name: string;
  colors: BrandingColors;
  Logo: ComponentType<LogoProps>;
}

const BrandingContext = createContext<BrandingContextValue | undefined>(
  undefined
);

/**
 * Applies branding colors as CSS custom properties on :root
 */
function applyCssVariables(colors: BrandingColors): void {
  const root = document.documentElement;
  root.style.setProperty("--ts-primary", colors.primary);
  root.style.setProperty("--ts-primary-hover", colors.primaryHover);
  root.style.setProperty("--ts-secondary", colors.secondary);
  root.style.setProperty("--ts-accent", colors.accent);
  root.style.setProperty("--ts-background", colors.background);
  root.style.setProperty("--ts-foreground", colors.foreground);
  root.style.setProperty("--ts-muted", colors.muted);
  root.style.setProperty("--ts-border", colors.border);
  root.style.setProperty("--ts-card", colors.card);
}

export const BrandingProvider = ({ children }: PropsWithChildren) => {
  const tenantId = useAppSelector(({ auth }) => auth.tenantId);
  const branding = useMemo(() => {
    if (tenantId && brandingByTenantId[tenantId]) {
      return brandingByTenantId[tenantId];
    }
    return defaultBranding;
  }, [tenantId]);

  // Apply CSS variables whenever branding changes
  useEffect(() => {
    applyCssVariables(branding.colors);
  }, [branding.colors]);

  const value: BrandingContextValue = useMemo(
    () => ({
      tenantId,
      name: branding.name,
      colors: branding.colors,
      Logo: branding.logo ?? UnlikelyAILogo,
    }),
    [tenantId, branding]
  );

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

/**
 * Hook to access organization branding (colors, logo).
 * Must be used within a BrandingProvider.
 */
export const useBranding = (): BrandingContextValue => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error("useBranding must be used within a BrandingProvider");
  }
  return context;
};
